// app/frontend/src/pages/interface/components/codeEditor/MonacoSuggestionController.ts
import type { StagedCodeEdit } from "@/components/aiBridge";
import { findUniqueRange, offsetToPosition } from "@/components/aiBridge/codeEditSearch";

// Loose Monaco types — @monaco-editor/react hands these in untyped at onMount.
type IEditor = any; // monaco.editor.IStandaloneCodeEditor
type Monaco = any; // typeof monaco

export interface SuggestionControllerOptions {
    /** Called after an accept lands; carries the file and its new full content. */
    onAccept: (fileId: number, newContent: string) => void;
    /** Reports the user's resolution back to the adapter. */
    resolve: (editId: string, status: "accepted" | "rejected" | "stale", reason?: string) => void;
}

interface RenderedEdit {
    edit: StagedCodeEdit;
    decorationIds: string[];
    viewZoneId: string | null;
    zoneDomNode: HTMLElement | null;
}

/**
 * Renders agent-staged edits in a Monaco editor as (a) a quickfix code action
 * ("Apply Agent suggestion" / "Dismiss Agent suggestion") and (b) a visible
 * inline diff: the old range highlighted, with the proposed replacement shown
 * in a view zone below plus accept/reject buttons.
 *
 * Edits are keyed by editId and scoped to a fileId; only edits for the active
 * file are rendered, but all staged edits are retained so switching back to a
 * file re-renders its pending suggestions.
 */
export class MonacoSuggestionController {
    private staged = new Map<string, StagedCodeEdit>();
    private rendered = new Map<string, RenderedEdit>();
    private activeFileId: number | null = null;
    private codeActionDisposable: { dispose(): void } | null = null;
    private acceptCommandId: string | null = null;
    private rejectCommandId: string | null = null;

    constructor(
        private editor: IEditor,
        private monaco: Monaco,
        private opts: SuggestionControllerOptions
    ) {
        this.registerCommands();
        this.registerCodeActionProvider();
    }

    setActiveFile(fileId: number | null): void {
        if (fileId === this.activeFileId) return;
        this.activeFileId = fileId;
        // Re-render: clear current decorations/zones, then render edits for this file.
        for (const editId of Array.from(this.rendered.keys())) {
            this.unrender(editId);
        }
        for (const edit of this.staged.values()) {
            if (edit.fileId === fileId) this.render(edit);
        }
    }

    stage(edit: StagedCodeEdit): void {
        this.staged.set(edit.editId, edit);
        if (edit.fileId === this.activeFileId) this.render(edit);
    }

    accept(editId: string): void {
        const edit = this.staged.get(editId);
        if (!edit) return;
        const model = this.editor.getModel();
        if (!model) return;

        const content: string = model.getValue();
        // Re-resolve the range against current content (the user may have typed).
        let start = edit.startOffset;
        let end = edit.endOffset;
        if (content.slice(start, end) !== edit.old_string) {
            const found = findUniqueRange(content, edit.old_string);
            if (found.status !== "ok") {
                this.clear(editId);
                this.opts.resolve(editId, "stale", `old_string no longer uniquely present (${found.status})`);
                return;
            }
            start = found.start;
            end = found.end;
        }

        const startPos = offsetToPosition(content, start);
        const endPos = offsetToPosition(content, end);
        const range = new this.monaco.Range(
            startPos.lineNumber,
            startPos.column,
            endPos.lineNumber,
            endPos.column
        );

        // pushEditOperations keeps the change on Monaco's native undo stack.
        model.pushEditOperations(
            [],
            [{ range, text: edit.new_string, forceMoveMarkers: true }],
            () => null
        );

        this.clear(editId);
        this.opts.onAccept(edit.fileId, model.getValue());
        this.opts.resolve(editId, "accepted");
    }

    reject(editId: string): void {
        const edit = this.staged.get(editId);
        this.clear(editId);
        if (edit) this.opts.resolve(editId, "rejected");
    }

    dispose(): void {
        for (const editId of Array.from(this.rendered.keys())) this.unrender(editId);
        this.staged.clear();
        this.codeActionDisposable?.dispose();
    }

    // ── internals ──────────────────────────────────────────────────────────

    private clear(editId: string): void {
        this.unrender(editId);
        this.staged.delete(editId);
    }

    private rangeOf(edit: StagedCodeEdit): any {
        const content: string = this.editor.getModel()?.getValue() ?? "";
        const startPos = offsetToPosition(content, edit.startOffset);
        const endPos = offsetToPosition(content, edit.endOffset);
        return new this.monaco.Range(
            startPos.lineNumber,
            startPos.column,
            endPos.lineNumber,
            endPos.column
        );
    }

    private render(edit: StagedCodeEdit): void {
        if (this.rendered.has(edit.editId)) this.unrender(edit.editId);
        const range = this.rangeOf(edit);

        // (a) decoration highlighting the old range
        const decorationIds: string[] = this.editor.deltaDecorations(
            [],
            [
                {
                    range,
                    options: {
                        className: "cnx-agent-old",
                        inlineClassName: "cnx-agent-old-inline",
                        hoverMessage: { value: edit.rationale ?? "Agent suggestion" },
                        // A marker-less lightbulb hint: show a glyph in the margin.
                        glyphMarginClassName: "cnx-agent-glyph"
                    }
                }
            ]
        );

        // (b) view zone showing the proposed replacement + accept/reject buttons
        let viewZoneId: string | null = null;
        const zoneDomNode = document.createElement("div");
        zoneDomNode.className = "cnx-agent-zone";
        zoneDomNode.innerHTML = `
            <div class="cnx-agent-new"></div>
            <div class="cnx-agent-actions">
                <button type="button" data-cnx-accept>✓ Accept</button>
                <button type="button" data-cnx-reject>✗ Reject</button>
            </div>`;
        const newEl = zoneDomNode.querySelector(".cnx-agent-new") as HTMLElement;
        newEl.textContent = edit.new_string;
        // Make the zone interactive. Monaco renders view-zone DOM in a layer the
        // editor's own mouse handler also processes; without this the buttons are
        // visible but un-clickable. zIndex/pointerEvents lift the node above the
        // text layer; stopping mousedown propagation prevents Monaco from
        // hijacking the gesture (cursor move / focus steal) before the click fires.
        zoneDomNode.style.pointerEvents = "auto";
        zoneDomNode.style.zIndex = "10";
        zoneDomNode.addEventListener("mousedown", (e) => e.stopPropagation());
        const acceptBtn = zoneDomNode.querySelector("[data-cnx-accept]") as HTMLElement | null;
        const rejectBtn = zoneDomNode.querySelector("[data-cnx-reject]") as HTMLElement | null;
        acceptBtn?.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.accept(edit.editId);
        });
        rejectBtn?.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.reject(edit.editId);
        });

        this.editor.changeViewZones((accessor: any) => {
            viewZoneId = accessor.addZone({
                afterLineNumber: range.endLineNumber,
                heightInLines: Math.max(1, edit.new_string.split("\n").length) + 1,
                domNode: zoneDomNode,
                // Let mouse events reach our buttons instead of Monaco
                // preventing them (default suppresses mousedown over the zone).
                suppressMouseDown: false
            });
        });

        this.rendered.set(edit.editId, { edit, decorationIds, viewZoneId, zoneDomNode });
    }

    private unrender(editId: string): void {
        const r = this.rendered.get(editId);
        if (!r) return;
        this.editor.deltaDecorations(r.decorationIds, []);
        if (r.viewZoneId) {
            this.editor.changeViewZones((accessor: any) => accessor.removeZone(r.viewZoneId));
        }
        this.rendered.delete(editId);
    }

    private registerCommands(): void {
        this.acceptCommandId = this.editor.addCommand(0, (_: unknown, editId: string) => this.accept(editId)) ?? null;
        this.rejectCommandId = this.editor.addCommand(0, (_: unknown, editId: string) => this.reject(editId)) ?? null;
    }

    private registerCodeActionProvider(): void {
        const languages: string[] = this.monaco.languages.getLanguages().map((l: any) => l.id);
        const provider = {
            provideCodeActions: (model: any, range: any) => {
                const actions: any[] = [];
                for (const { edit } of this.rendered.values()) {
                    const r = this.rangeOf(edit);
                    if (!this.monaco.Range.areIntersectingOrTouching(r, range)) continue;
                    actions.push(
                        {
                            title: "Apply Agent suggestion",
                            kind: "quickfix",
                            isPreferred: true,
                            ...(this.acceptCommandId ? { command: { id: this.acceptCommandId, title: "Apply Agent suggestion", arguments: [edit.editId] } } : {})
                        },
                        {
                            title: "Dismiss Agent suggestion",
                            kind: "quickfix",
                            ...(this.rejectCommandId ? { command: { id: this.rejectCommandId, title: "Dismiss Agent suggestion", arguments: [edit.editId] } } : {})
                        }
                    );
                }
                return { actions, dispose: () => undefined };
            }
        };
        // Register once across all languages so it works for every open file.
        this.codeActionDisposable = {
            dispose: () => {
                disposables.forEach((d) => d.dispose());
            }
        };
        const disposables = languages.map((lang) =>
            this.monaco.languages.registerCodeActionProvider(lang, provider)
        );
    }
}
